
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordRequirements } from "./PasswordRequirements";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Eye, EyeOff } from "lucide-react";

export interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

interface SignUpFormProps {
  formData: FormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  selectedPriceId?: string | null;
  emailError?: string;
}

export const SignUpForm = ({
  formData,
  onChange,
  onSubmit,
  loading,
  selectedPriceId,
  emailError,
}: SignUpFormProps) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  
  // Password matching validation
  const passwordsMatch = formData.password === formData.confirmPassword;
  const showPasswordMatchError = formData.confirmPassword.length > 0 && !passwordsMatch;
  const inputClasses = "border-gray-300 focus:ring-primary focus:border-primary transition-all duration-200";
  
  const formControls = [
    {
      id: "first-name",
      name: "firstName",
      label: "First Name",
      value: formData.firstName,
      placeholder: "John",
      type: "text",
    },
    {
      id: "last-name",
      name: "lastName",
      label: "Last Name",
      value: formData.lastName,
      placeholder: "Doe",
      type: "text",
    },
    {
      id: "company-name",
      name: "companyName",
      label: "Company/Organization Name",
      value: formData.companyName,
      placeholder: "Acme Studios",
      type: "text",
    },
    {
      id: "email-signup",
      name: "email",
      label: "Email",
      value: formData.email,
      placeholder: "email@example.com",
      type: "email",
    },
    {
      id: "password-signup",
      name: "password",
      label: "Password",
      value: formData.password,
      placeholder: "••••••••••",
      type: showPassword ? "text" : "password",
      showToggle: true,
      toggleShow: () => setShowPassword(!showPassword),
      showValue: showPassword,
    },
    {
      id: "confirm-password-signup",
      name: "confirmPassword",
      label: "Confirm Password",
      value: formData.confirmPassword,
      placeholder: "••••••••••",
      type: showConfirmPassword ? "text" : "password",
      showToggle: true,
      toggleShow: () => setShowConfirmPassword(!showConfirmPassword),
      showValue: showConfirmPassword,
    }
  ] as Array<{
    id: string;
    name: string;
    label: string;
    value: string;
    placeholder: string;
    type: string;
    showToggle?: boolean;
    toggleShow?: () => void;
    showValue?: boolean;
  }>;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <form 
        onSubmit={onSubmit} 
        className="space-y-4"
      >
        <div className="space-y-4">
          {formControls.map((control, i) => (
            <motion.div
              key={control.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
              className="space-y-2"
            >
              <Label htmlFor={control.id} className="font-medium">
                {control.label} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input 
                  id={control.id}
                  name={control.name}
                  type={control.type} 
                  placeholder={control.placeholder} 
                  value={control.value} 
                  onChange={onChange} 
                  disabled={loading}
                  className={`${inputClasses} ${
                    control.id === 'email-signup' && emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  } ${
                    control.id === 'confirm-password-signup' && showPasswordMatchError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  } ${control.showToggle ? 'pr-10' : ''}`}
                  required 
                />
                {control.showToggle && (
                  <button
                    type="button"
                    disabled={loading}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={control.toggleShow}
                  >
                    {control.showValue ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                )}
              </div>
              {control.id === "email-signup" && emailError && (
                <div className="text-sm text-red-600 mt-1">
                  {emailError}
                </div>
              )}
              {control.id === "password-signup" && (
                <PasswordRequirements password={formData.password} />
              )}
              {control.id === "confirm-password-signup" && showPasswordMatchError && (
                <div className="text-sm text-red-600 mt-1">
                  Passwords do not match
                </div>
              )}
              {control.id === "confirm-password-signup" && formData.confirmPassword.length > 0 && passwordsMatch && (
                <div className="text-sm text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Passwords match
                </div>
              )}
            </motion.div>
          ))}
        </div>
        
        <div className="text-sm text-gray-500 mt-4 mb-2">
          <span className="text-red-500">*</span> All fields are required
        </div>
        
        <div className="bg-amber-50 border border-amber-100 rounded-md p-3">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div className="ml-2 text-sm text-amber-800">
              By providing us with your information you are consenting to the collection and use of your information in accordance with our{" "}
              <Link 
                to="/terms-of-service" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link 
                to="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Privacy Policy
              </Link>.
            </div>
          </div>
        </div>
        
        <Button
          type="submit"
          className="w-full py-5 bg-primary hover:bg-primary/90 transition-colors"
          disabled={loading || !selectedPriceId}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Creating account...
            </>
          ) : (
            "Subscribe Now"
          )}
        </Button>
      </form>
    </motion.div>
  );
};
//commentss